// NPI registry lookup — the wire contract for GET /api/npi/lookup.
//
// This shape previously existed TWICE: as an object literal in apps/backend/src/routes/npi.ts and
// as a hand-copied, unexported interface in apps/mobile/services/api.ts, with no runtime check on
// either side (the client did `return await response.json()` and asserted the type). That is the
// duplication class this package exists to remove — the two copies had already diverged in intent,
// since the client never read `credential` and the server kept sending it.
//
// The backend types its 200 payload with NpiLookupResponse; the client parses with
// NpiLookupResponseSchema so a contract change surfaces at the boundary rather than as a blank
// name field halfway through onboarding.

import { z } from 'zod';

export const NpiLookupResponseSchema = z.object({
  /** Provider legal first name, title-cased from the registry's ALL-CAPS storage. */
  firstName: z.string(),
  /** Provider legal last name, title-cased. */
  lastName: z.string(),
  // The three below are ABSENT, never empty-string, when the registry does not determine them.
  // An absent field means "prefill nothing"; '' would render as a chosen picker value.
  /** Canonical Q4uDoc specialty derived from the provider's PRIMARY NUCC taxonomy. */
  specialty: z.string().optional(),
  /** Practice city, title-cased. Present only for US LOCATION addresses. */
  city: z.string().optional(),
  /** Two-letter US state code. Present only for US LOCATION addresses. */
  state: z.string().optional(),
});

export type NpiLookupResponse = z.infer<typeof NpiLookupResponseSchema>;

// Error responses are deliberately NOT modelled here yet. The `code` values
// (INVALID_NPI / NPI_NOT_FOUND / NPI_NOT_INDIVIDUAL / NPI_ALREADY_IN_USE / NPI_LOCKED /
// NPI_LOOKUP_UNAVAILABLE) are documented at the two routes that emit them, and the mobile client
// still branches on string literals. A shared enum belongs here the moment a consumer imports it —
// adding one now would be an export nobody uses.
